/**
 * FICA / self-employment tax and the Additional Medicare Tax (spec §9).
 * On EARNED income only (wages + self-employment). Retirees with no earned
 * income owe none of this — but pre-retirement and part-time work do.
 */
import type { FilingStatus } from '../../model/types';
import { federalTaxYear } from '../data/federal';

export interface FicaResult {
  socialSecurityTax: number;
  medicareTax: number;
  additionalMedicareTax: number;
  total: number;
}

/**
 * @param wages         W-2 wages (employee share of FICA)
 * @param seNetProfit   net self-employment profit (subject to SE tax)
 * @param inflationRate indexes the SS wage base for future years (wage-indexed
 *                      in law); the Additional Medicare threshold stays fixed
 *                      (statutory).
 */
export function computeFica(
  year: number,
  filingStatus: FilingStatus,
  wages: number,
  seNetProfit: number,
  inflationRate?: number,
): FicaResult {
  const fy = federalTaxYear(year, inflationRate);
  const f = fy.fica;

  // Self-employment tax applies to 92.35% of net SE profit.
  const seBase = Math.max(0, seNetProfit) * 0.9235;

  // Social Security portion is capped at the wage base; wages fill the base first.
  const ssWagePortion = Math.min(wages, f.ssWageBase);
  const ssBaseRemaining = Math.max(0, f.ssWageBase - wages);
  const ssSePortion = Math.min(seBase, ssBaseRemaining);
  const socialSecurityTax =
    ssWagePortion * f.ssRateEmployee + ssSePortion * f.ssRateSelfEmployment;

  // Medicare has no cap.
  const medicareTax =
    wages * f.medicareRateEmployee + seBase * f.medicareRateSelfEmployment;

  // Additional Medicare Tax: 0.9% on (wages + SE base) over a fixed threshold.
  const combinedEarned = wages + seBase;
  const threshold = f.additionalMedicareThreshold[filingStatus];
  const additionalMedicareTax = Math.max(0, combinedEarned - threshold) * f.additionalMedicareRate;

  return {
    socialSecurityTax,
    medicareTax,
    additionalMedicareTax,
    total: socialSecurityTax + medicareTax + additionalMedicareTax,
  };
}
